import { useTranslation } from 'react-i18next'
import { ArrowLeft, Coins } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tournament } from '@brochure/shared'
import { LocalizedLink } from '@/components/LocalizedLink'
import { formatTournamentDate } from '@/lib/stoccopong-format'
import { StatusStamp } from '@/components/stoccopong/StatusStamp'

type TournamentHeaderProps = {
  tournament: Tournament
  isLive: boolean
  wallet?: { name: string; balance: number }
}

/**
 * Intestazione della pagina torneo: ritorno a tutti i tornei, data, nome, stato, badge LIVE (solo a torneo in corso
 * e socket aperto) e il portafoglio dell'ospite come un piccolo biglietto. Titolo contenuto: i nomi dei tornei possono
 * essere lunghi e su un telefono non devono prendersi mezzo schermo.
 */
export function TournamentHeader({ tournament, isLive, wallet }: TournamentHeaderProps) {
  const { t, i18n } = useTranslation()
  return (
    <header>
      <div className="flex items-center justify-between gap-3">
        <LocalizedLink
          to="stoccoPong"
          className="-ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 font-body text-sm font-semibold text-ink-soft transition-colors hover:bg-ink/5 hover:text-ink"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('stoccoPong.allTournaments')}
        </LocalizedLink>
        {/* Stockbet ha senso solo con le partite in calendario: da torneo avviato in poi. */}
        {(tournament.status === 'active' || tournament.status === 'completed') && (
          <LocalizedLink
            to="stoccoPongStockbet"
            params={{ id: tournament.id }}
            data-testid="stockbet-link"
            className="inline-flex min-h-11 items-stretch overflow-hidden rounded-lg border-2 border-ink bg-ink font-body text-sm font-semibold text-paper shadow-[3px_3px_0_var(--color-sun)] transition-[transform,box-shadow] duration-200 ease-out-expo hover:-translate-y-px hover:shadow-[4px_4px_0_var(--color-sun)] active:translate-y-0 active:shadow-[1px_1px_0_var(--color-sun)]"
          >
            <span className="flex items-center bg-sun px-2 text-ink">
              <Coins className="size-4" aria-hidden="true" />
            </span>
            <span className="flex items-center border-l-2 border-dashed border-paper/30 px-3">{t('stockbet.title')}</span>
          </LocalizedLink>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="font-body text-sm font-semibold text-ink-soft">{formatTournamentDate(tournament.date, i18n.language)}</p>
          <h1 className="hero-title mt-1 text-[clamp(2.25rem,1.4rem+4.5vw,5rem)] text-ink">{tournament.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusStamp status={tournament.status} />
            {isLive && tournament.status === 'active' && (
              <span data-testid="live" className="inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.14em] text-coral-ink">
                <span className="size-2 rounded-full bg-coral motion-safe:animate-pulse" aria-hidden="true" />
                {t('stoccoPong.live')}
              </span>
            )}
          </div>
        </div>

        {wallet && <WalletTicket name={wallet.name} balance={wallet.balance} />}
      </div>
    </header>
  )
}

/** Portafoglio dell'ospite come un piccolo biglietto: cedola moneta a sinistra, nome e saldo oltre la perforazione. */
export function WalletTicket({ name, balance, className }: { name: string; balance: number; className?: string }) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="wallet"
      className={cn('flex items-stretch overflow-hidden rounded-xl border-2 border-ink bg-paper font-body text-ink shadow-[3px_3px_0_var(--color-ink)]', className)}
    >
      <span className="flex items-center bg-sun/25 px-3 text-sun-ink">
        <Coins className="size-5" aria-hidden="true" />
      </span>
      <span className="flex flex-col justify-center border-l-2 border-dashed border-ink/30 px-3 py-1.5">
        <span className="text-xs font-semibold text-ink-soft">{name}</span>
        <span className="text-base font-semibold tabular-nums">{t('stoccoPong.wallet.coins', { count: balance })}</span>
      </span>
    </div>
  )
}
